import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";

import ClientName from "./FormComponents/New/ClientName";
import ClientAddress from "./FormComponents/New/ClientAddress";
import ClientContact from "./FormComponents/New/ClientContact";

import { postClient, getCacheClients } from "../API/clientAPI";
import { getNationalAddresses } from "../API/nationalAddressAPI";
import { customAlphabet } from "nanoid";
import { BaseFormWrapper } from "../../components/shared/BaseFormWrapper";
import { getUsersByRoles } from "../../components/API/userAPI";
import { StyledHr } from "../../components/shared/CustomDivider";
import { clientValidationSchema } from "./utils/clientValidationSchema";

function CreateClient() {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const generateId = customAlphabet(alphabet, 8); // Creates a function to generate an 8-character ID
  const navigate = useNavigate();
  const [client, setClient] = useState({
    name: "",
    addresses: [{ id: generateId(), area: "", billing: true, house_unit_street: "", barangay: "", city: "", metro_province: "", zipcode: "" }],
    contacts: [{ id: generateId(), firstname: "", lastname: "", billing: true, birthday: "", numbers: [{ id: generateId(), type: "mobile", number: "" }], emails: [{ id: generateId(), email: "" }] }],
    category: "", parent_client: "", service_types: [], location_type: "", regular: false, work_permit_required: false,
    account_owner: { id: "", firstname: "", lastname: "" }, price: "", payment_terms: "", contract_start: null, contract_end: null,
  });
  const [nationalAddresses, setNationalAddresses] = useState([]);
  const [clientsCache, setClientsCache] = useState([]);
  const [selectedParentClient, setSelectedParentClient] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [accountOwnerOptions, setAccountOwnerOptions] = useState([]);
  const [isLoadingOwners, setIsLoadingOwners] = useState(false);

  // factory for a fresh client payload
  const createEmptyClient = () => ({
    name: "",
    addresses: [
      {
        id: generateId(),
        area: "",
        billing: true,
        house_unit_street: "",
        barangay: "",        
        city: "",
        metro_province: "",
        zipcode: "",
      },
    ],
    contacts: [
      {
        id: generateId(),
        firstname: "",
        lastname: "",
        billing: true,
        birthday: "",
        numbers: [
          { id: generateId(), type: "mobile", number: "" },
        ],
        emails: [
          { id: generateId(), email: "" },
        ],
      },
    ],
    category: "",
    parent_client: "",
    service_types: [],
    location_type: "",
    regular: false,
    work_permit_required: false,
    account_owner: { id: "", firstname: "", lastname: "" },
    price: "",
    payment_terms: "",
    contract_start: null,
    contract_end: null,
  });

  const clientFilter = createFilterOptions({
    matchFrom: "any",
    stringify: (option) => option?.name || "",
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getUsersByRoles("customer_service", { match: "any", limit: 200 });
        const list = Array.isArray(res) ? res : (res?.users || []);
        const options = (list || [])
          .map(u => ({
            id: u.id ?? u.user_id ?? u.username ?? "",
            firstname: u.firstname ?? "",
            lastname: u.lastname ?? "",
            label: [u.lastname, u.firstname].filter(Boolean).join(", "),
          }))
          .filter(o => o.id && (o.firstname || o.lastname))
          .sort((a, b) => a.label.localeCompare(b.label));
        if (alive) setAccountOwnerOptions(options);
      } catch {
        if (alive) setAccountOwnerOptions([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    async function loadNational() {
      try {
        const data = await getNationalAddresses();
        setNationalAddresses(data);
      } catch (e) { console.error(e); }
    }
    loadNational();
    async function loadClients() {
      try { setClientsCache(await getCacheClients()); } catch {};
    }
    loadClients();
  }, []);

  const handleAddressChange = (i, field, val) => {
    setClient(prev => {
      const addrs = [...prev.addresses]; 
      addrs[i] = { ...addrs[i], [field]: val }; 
      return { ...prev, addresses: addrs };
    });
  };
  const handleCityChange = (i, val) => handleAddressChange(i, 'city', val);
  const handleBarangayChange = (i, val) => handleAddressChange(i, 'barangay', val);
  const handleAddressCheckboxChange = idx => setClient(prev => ({ ...prev, addresses: prev.addresses.map((a,i)=>({ ...a, billing: i===idx })) }));
  const handleAddAddress = () => setClient(prev=>({ ...prev, addresses: [...prev.addresses, { id: generateId(), area: "", billing: false, house_unit_street: "", barangay: "", city: "", metro_province: "", zipcode: "" }] }));
  const handleRemoveAddress = idx => setClient(prev=>({ ...prev, addresses: prev.addresses.filter((_,i)=>i!==idx) }));

  const service_types = [
    "General Pest Control",
    "Termite Control",
    "QPMS",
    "Disinfection",
  ];

  const locationTypeOptions = [
    "Bank",
    "Bar",
    "BPO",
    "Commercial Complex",
    "Commissary",
    "Condo Unit",
    "Condominium",
    "Department Store",
    "Embassy",
    "Food Manufacturing",
    "Government Office",
    "Hospital or Clinic",
    "Hotel",
    "Industrial Plant",
    "Mall",
    "Manufacturing",
    "Office",
    "Parks and Parking",
    "Residential (Apartment)",
    "Residential (Single Unit)",
    "Restaurant or Café",
    "School",
    "Sports Center",
    "Supermarket",
    "Vehicle",
    "Village",
    "Warehouse",
    "Wet Market",
    "Other"
  ];

  const handleClientNameChange = (value) => {
    setClient((prev) => ({
      ...prev,
      name: value,
    }));
  };

  const handleWorkPermitRequiredChange = (value) => {
    // value comes as string from RadioGroup; normalize to boolean
    const boolVal = value === "true";
    setClient((prev) => ({
      ...prev,
      work_permit_required: boolVal,
    }));
  };

  // Handler to update contact fields
  const handleContactChange = (index, field, value) => {
    setClient((prev) => ({
      ...prev,
      contacts: prev.contacts.map((contact, i) =>
        i === index ? { ...contact, [field]: value } : contact
      ),
    }));
  };

  const handleAddContact = () => {
    setClient((prev) => ({
      ...prev,
      contacts: [
        ...prev.contacts,
        {
          id: generateId(),
          firstname: "",
          lastname: "",
          billing: false,
          birthday: "",
          numbers: [
            {
              id: generateId(),
              type: "mobile",
              number: "",
            },
          ],
          emails: [
            {
              id: generateId(),
              email: "",
            },
          ],
        },
      ],
    }));
  };

  // Handler to remove a contact person
  const handleRemoveContact = (index) => {
    setClient((prev) => {
      const updatedContacts = prev.contacts.filter(
        (_, contactIndex) => contactIndex !== index
      );

      // If only one address remains, set its billing field to true
      if (updatedContacts.length === 1) {
        updatedContacts[0] = { ...updatedContacts[0], billing: true };
      }

      return {
        ...prev,
        contacts: updatedContacts,
      };
    });
  };

  const handleContactCheckboxChange = (index) => {
    setClient((prev) => ({
      ...prev,
      contacts: prev.contacts.map((contact, i) => ({
        ...contact,
        billing: i === index, // Set billing_address to true only for the selected index
      })),
    }));
  };

  const handleContactDetailsChange = (
    contactIndex,
    field,
    subIndex,
    subField,
    value
  ) => {
    setClient((prev) => ({
      ...prev,
      contacts: prev.contacts.map((contact, i) =>
        i === contactIndex
          ? {
            ...contact,
            [field]: contact[field].map((item, j) =>
              j === subIndex ? { ...item, [subField]: value } : item
            ),
          }
          : contact
      ),
    }));
  };

  const handleAddNumber = (contactIndex) => {
    setClient((prev) => ({
      ...prev,
      contacts: prev.contacts.map((contact, i) =>
        i === contactIndex
          ? {
            ...contact,
            numbers: [
              ...contact.numbers,
              { id: generateId(), type: "mobile", number: "" },
            ],
          }
          : contact
      ),
    }));
  };

  const handleRemoveNumber = (contactIndex, numberIndex) => {
    setClient((prev) => ({
      ...prev,
      contacts: prev.contacts.map((contact, i) =>
        i === contactIndex
          ? {
            ...contact,
            numbers:
              contact.numbers.length > 1
                ? contact.numbers.filter((_, j) => j !== numberIndex)
                : contact.numbers, // Keep at least one number if only one remains
          }
          : contact
      ),
    }));
  };

  const handleAddEmail = (contactIndex) => {
    setClient((prev) => ({
      ...prev,
      contacts: prev.contacts.map((contact, i) =>
        i === contactIndex
          ? {
            ...contact,
            emails: [...contact.emails, { id: generateId(), email: "" }],
          }
          : contact
      ),
    }));
  };

  const handleRemoveEmail = (contactIndex, emailIndex) => {
    setClient((prev) => ({
      ...prev,
      contacts: prev.contacts.map((contact, i) =>
        i === contactIndex
          ? {
            ...contact,
            emails:
              contact.emails.length > 1
                ? contact.emails.filter((_, j) => j !== emailIndex)
                : contact.emails, // Keep at least one number if only one remains
          }
          : contact
      ),
    }));
  };

  const handleCategoryChange = (value) => {
    setClient((prev) => ({
      ...prev,
      category: value,
    }));
  };

  const handleSelectedParentClient = (event, value) => {
    setSelectedParentClient(value || null); // Update the selected client
    setClient((prev) => ({
      ...prev,
      parent_client: value,
    }));
  };

  const handleServiceTypeChange = (e, formattedOption) => {
    setClient((prev) => {
      const isChecked = e.target.checked; // Extract checked status
      const updatedServiceTypes = isChecked
        ? [...prev.service_types, formattedOption] // Add formattedOption if checked
        : prev.service_types.filter((option) => option !== formattedOption); // Remove if unchecked

      // Return early if there's no change to prevent unnecessary re-renders
      if (updatedServiceTypes === prev.service_types) {
        return prev;
      }

      return {
        ...prev, // Preserve other jobOrder properties
        service_types: updatedServiceTypes,
      };
    });
  };

  const handleLocationTypeChange = (value) => {
    setClient((prev) => ({
      ...prev,
      location_type: value,
    }));
  };

  const handleAccountOwnerChange = (ownerObj) => {
    setClient(prev => ({
      ...prev,
      account_owner: ownerObj || { id: "", firstname: "", lastname: "" },
    }));
  };

  const handlePriceChange = (value) => {
    setClient((prev) => ({
      ...prev,
      price: value,
    }));
  };

  const handlePaymentTermsChange = (value) => {
    setClient((prev) => ({
      ...prev,
      payment_terms: value,
    }));
  };

  // When contract start changes, only default end if it’s empty or would be before/equal to start
  const handleContractStartChange = (date) => {
    setClient(prev => {
      let end = prev.contract_end;
      if (date) {
        // if there’s no end yet, or it’s <= new start, default to 1 yr later
        if (!prev.contract_end || prev.contract_end <= date) {
          const tmp = new Date(date);
          tmp.setFullYear(tmp.getFullYear() + 1);
          end = tmp;
        }
      } else {
        // clearing the start clears the end too
        end = null;
      }
      return { ...prev, contract_start: date, contract_end: end };
    });
  };

  // Let the user pick a custom end – we’ll validate it on submit
  const handleContractEndChange = (date) => {
    setClient(prev => ({ ...prev, contract_end: date }));
  };

  // const handleRegularChange = () => {
  //   setClient(prev => ({
  //     ...prev,
  //     // toggle on/off: if already true, clear to null; otherwise set true
  //     regular: prev.regular === true ? null : true,
  //   }));
  // };
  
  // const handleNonRegularChange = () => {
  //   setClient(prev => ({
  //     ...prev,
  //     // toggle on/off: if already false, clear to null; otherwise set false
  //     regular: prev.regular === false ? null : false,
  //   }));
  // };

  const handleRegularChange = (event) => {
    setClient((prev) => ({
      ...prev,
      regular: event.target.value === "true",
    }));
  };

  const normalizeOwner = (v) =>
  v && typeof v === "object"
    ? {
        id: v.id || "",
        firstname: v.firstname || "",
        lastname: v.lastname || "",
      }
    : { id: "", firstname: "", lastname: "" };

  // Utility function to clean up spaces (trim and remove multiple consecutive spaces)
  const cleanSpaces = (str) => {
    if (typeof str !== 'string') return str;
    return str.trim().replace(/\s+/g, ' ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const storedUser = localStorage.getItem("user");
    const user = storedUser ? JSON.parse(storedUser).username : "";

    const ao = normalizeOwner(client.account_owner);
    // Clean up all string fields before validation and submission
    const cleanedClient = {
      ...client,
      name: cleanSpaces(client.name),
      addresses: client.addresses.map(addr => ({
        ...addr,
        area: cleanSpaces(addr.area),
        house_unit_street: cleanSpaces(addr.house_unit_street),
        barangay: cleanSpaces(addr.barangay),
        city: cleanSpaces(addr.city),
        metro_province: cleanSpaces(addr.metro_province),
        zipcode: cleanSpaces(addr.zipcode),
      })),
      contacts: client.contacts.map(contact => ({
        ...contact,
        firstname: cleanSpaces(contact.firstname),
        lastname: cleanSpaces(contact.lastname),
        numbers: contact.numbers.map(num => ({
          ...num,
          number: cleanSpaces(num.number),
        })),
        emails: contact.emails.map(email => ({
          ...email,
          email: cleanSpaces(email.email),
        })),
      })),
      // account_owner: normalizeOwner(client.account_owner),  // we’ll handle below
      price: cleanSpaces(client.price),
      payment_terms: cleanSpaces(client.payment_terms),
      user_create: user,
      user_update: user,   // for creation, creator == updater
    };

    // ✅ Set account_owner directly on cleanedClient
    if (ao.id || ao.firstname || ao.lastname) {
      cleanedClient.account_owner = ao;   // keep when set
    } else {
      // optional: remove it entirely if empty
      delete cleanedClient.account_owner;
    }

    // ── DATE VALIDATION & NOTIFICATION ───────────────────────────────────────────
    // 1) both-or-none rule
    if ((cleanedClient.contract_start && !cleanedClient.contract_end) ||
        (!cleanedClient.contract_start && cleanedClient.contract_end)) {
      setErrors({
        ...errors,
        contract_start: cleanedClient.contract_start
          ? ""
          : "Required when end date is filled",
        contract_end: cleanedClient.contract_end
          ? ""
          : "Required when start date is filled",
      });
      setErrorMessage("Please fill in both Contract Start and End dates, or leave both empty.");
      return;
    }

    // 2) ordering rule
    if (cleanedClient.contract_start && cleanedClient.contract_end &&
        cleanedClient.contract_end <= cleanedClient.contract_start) {
      setErrors({
        ...errors,
        contract_end: "End date must be after start date",
      });
      setErrorMessage("Contract End must be strictly after Contract Start.");
      return;
    }
    // clear any previous date errors/notifications
    setErrors(prev => ({ ...prev, contract_start: "", contract_end: "" }));
    setErrorMessage("");
    // ────────────────────────────────────────────────────────────────

    try {
      await clientValidationSchema(null, clientsCache).validate(cleanedClient, {
        abortEarly: false,
      });

      setErrors({});
      setErrorMessage("");
      setIsLoading(true);

      try {
        const response = await postClient(cleanedClient);

        if (response.ok) {
          setSuccessMessage("Client created successfully!");
          // CLEAR THE FORM
          setClient(createEmptyClient());
          setSelectedParentClient(null);
          setErrors({});
        }
      } catch (error) {
        setErrorMessage(
          `An error occurred while creating the Client: ${error.message}. Please check your network connection and try again.`
        );
      }
    } catch (validationErrors) {
      if (validationErrors.inner) {
        const formattedErrors = validationErrors.inner.reduce((acc, error) => {
          acc[error.path] = error.message;
          return acc;
        }, {});

        setErrors(formattedErrors);
      } else {
        setErrorMessage(
          validationErrors.message ||
          "Validation failed. Please check your input and try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BaseFormWrapper
      titleIcon={<SupervisedUserCircleIcon />}
      title="Client Retention"
      subTitle="Manage Clients"
      subTitleRoute="/manage-clients"
      formTitle="Create Client"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(e);
        }}
        noValidate
        autoComplete="off"
      >
        <Grid container>
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="h6">Basic Information</Typography>}
              />
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  padding: 3,
                  '&:last-child': {
                    paddingBottom: 3,
                  },
                }}
              >
                <Grid container spacing={2}>
                  {/* Client Name */}
                  <Grid item xs={12}>
                    <ClientName
                      clientName={client.name}
                      handleClientNameChange={handleClientNameChange}
                      loading={isLoading}
                      errors={errors}
                    />
                  </Grid>
                </Grid>
                <StyledHr />
                {/* Job Site Address */}
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <ClientAddress
                      clientAddresses={client.addresses}
                      nationalAddresses={nationalAddresses}
                      handleAddressChange={handleAddressChange}
                      handleCityChange={handleCityChange}
                      handleBarangayChange={handleBarangayChange}
                      handleAddressCheckboxChange={handleAddressCheckboxChange}
                      handleAddAddress={handleAddAddress}
                      handleRemoveAddress={handleRemoveAddress}
                      loading={isLoading}
                      errors={errors}
                    />
                  </Grid>
                </Grid>

                {/* Work Permit Required Toggle */}
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" size="small">
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.875rem", color: "gray", mb: 0.5 }}
                      >
                        Work Permit Required?
                      </Typography>
                      <RadioGroup
                        row
                        value={String(client.work_permit_required)}
                        onChange={(e) => handleWorkPermitRequiredChange(e.target.value)}
                      >
                        <FormControlLabel
                          value="true"
                          control={<Radio size="small" />}
                          label="Yes"
                          sx={{ mr: 3, "& .MuiTypography-root": { fontSize: "0.875rem" } }}
                          disabled={isLoading}
                        />
                        <FormControlLabel
                          value="false"
                          control={<Radio size="small" />}
                          label="No"
                          sx={{ "& .MuiTypography-root": { fontSize: "0.875rem" } }}
                          disabled={isLoading}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>

                <StyledHr />

                {/* Contact Person Section */}
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <ClientContact
                      clientContacts={client.contacts}
                      handleContactChange={handleContactChange}
                      handleAddContact={handleAddContact}
                      handleRemoveContact={handleRemoveContact}
                      handleContactCheckboxChange={handleContactCheckboxChange}
                      handleContactDetailsChange={handleContactDetailsChange}
                      handleAddNumber={handleAddNumber}
                      handleRemoveNumber={handleRemoveNumber}
                      handleAddEmail={handleAddEmail}
                      handleRemoveEmail={handleRemoveEmail}
                      loading={isLoading}
                      errors={errors}
                    />
                  </Grid>
                </Grid>
                <StyledHr />
                <Grid container sx={{ mt: 2 }}>
                  <Grid item xs={12}>
                    <CardHeader
                      title={
                        <Typography
                          variant="h6"
                          sx={{
                            paddingBottom: "0.5rem",
                            fontWeight: "bold",
                            textTransform: "uppercase",
                            fontSize: "1rem",
                          }}
                        >
                          Client Classifications
                        </Typography>
                      }
                      sx={{ pb: 0, pt: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                        <FormControl
                          fullWidth
                          size="small"
                          error={!!errors.category}
                        >
                          <InputLabel id="category-label">Category</InputLabel>
                          <Select
                            labelId="category-label"
                            value={client.category}
                            onChange={(e) =>
                              handleCategoryChange(e.target.value)
                            }
                            label="Category"
                          >
                            <MenuItem
                              value="VVIP"
                              sx={{ fontSize: "0.875rem" }}
                            >
                              VVIP
                            </MenuItem>
                            <MenuItem value="VIP" sx={{ fontSize: "0.875rem" }}>
                              VIP
                            </MenuItem>
                            <MenuItem
                              value="Gold"
                              sx={{ fontSize: "0.875rem" }}
                            >
                              Gold
                            </MenuItem>
                            <MenuItem
                              value="Silver"
                              sx={{ fontSize: "0.875rem" }}
                            >
                              Silver
                            </MenuItem>
                            <MenuItem
                              value="Bronze"
                              sx={{ fontSize: "0.875rem" }}
                            >
                              Bronze
                            </MenuItem>
                          </Select>
                          {!!errors.category && (
                            <FormHelperText>{errors.category}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={8} lg={6} xl={6}>
                        <Autocomplete
                          options={clientsCache}
                          getOptionLabel={(client) => client?.name || ""} // Fallback to an empty string
                          isOptionEqualToValue={(option, value) => option?.id === value?.id}
                          filterOptions={(options, params) => clientFilter(options, params)}
                          openOnFocus
                          autoHighlight
                          value={selectedParentClient} // Controlled component
                          onChange={(event, newValue) =>
                            handleSelectedParentClient(event, newValue)
                          } // Updates selected value
                          renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                              <div style={{ display: "flex", flexDirection: "column" }}>
                                <span>{option.name}</span>
                                <small style={{ opacity: 0.7 }}>ID: {option.id}</small>
                              </div>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Parent Client"
                              variant="outlined"
                              fullWidth
                              size="small"
                              error={!!errors.parent_client}
                              helperText={errors.parent_client}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FormControl component="fieldset" error={!!errors.service_types} sx={{ width: '100%' }}>
                          <Typography
                            variant="body2"
                            sx={{
                              paddingBottom: "0.5rem", // Adjust spacing as needed
                              fontWeight: "normal", // Remove bold for label-like appearance
                              textTransform: "none", // Keep natural casing if preferred
                              fontSize: "0.875rem", // Smaller font size for a label
                              color: "gray", // Change the text color to gray
                            }}
                          >
                            Service Type
                          </Typography>
                          <Grid container spacing={1}>
                            {service_types.map((option) => {
                              const formattedOption = option.toLowerCase().replace(/\s+/g, "_");
                              return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={formattedOption}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        name={formattedOption}
                                        checked={client.service_types.includes(formattedOption)}
                                        onChange={(e) => handleServiceTypeChange(e, formattedOption)}
                                      />
                                    }
                                    label={option}
                                    sx={{
                                      margin: 0, // Remove default margins
                                      width: '100%', // Take full width of grid item
                                      alignItems: "center", // Align items properly
                                      "& .MuiTypography-root": {
                                        fontSize: "0.875rem", // Smaller label text size
                                      },
                                    }}
                                  />
                                </Grid>
                              );
                            })}
                          </Grid>
                          {!!errors.service_types && (
                            <FormHelperText>{errors.service_types}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                        <FormControl fullWidth size="small" error={!!errors.location_type}>
                          <InputLabel id="location-type-label">
                            Location Type
                          </InputLabel>
                          <Select
                            labelId="location-type-label"
                            value={client.location_type}
                            onChange={e => handleLocationTypeChange(e.target.value)}
                            label="Location Type"
                            size="small"
                          >
                            {locationTypeOptions.map(type => (
                              <MenuItem key={type} value={type} sx={{ fontSize: "0.875rem" }}>
                                {type}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.location_type && (
                            <FormHelperText>{errors.location_type}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                        <FormControl fullWidth size="small" error={!!errors?.account_owner}>
                          <InputLabel id="account-owner-label">Account Owner</InputLabel>
                          <Select
                            labelId="account-owner-label"
                            value={client.account_owner?.id || ""}            // controlled by id
                            onChange={(e) => {
                              if (!e.target.value) {
                                // User picked “None”
                                handleAccountOwnerChange({ id: "", firstname: "", lastname: "" });
                                return;
                              }
                              const sel = accountOwnerOptions.find(o => o.id === e.target.value);
                              handleAccountOwnerChange(sel ? {
                                id: sel.id, firstname: sel.firstname, lastname: sel.lastname
                              } : { id: "", firstname: "", lastname: "" });
                            }}
                            label="Account Owner"
                            size="small"
                          >
                            <MenuItem value=""><em>None</em></MenuItem>
                            {accountOwnerOptions.map(o => (
                              <MenuItem key={o.id} value={o.id}>{`${o.lastname}, ${o.firstname}`}</MenuItem>
                            ))}
                          </Select>
                          {!!errors?.account_owner && <FormHelperText>{errors.account_owner}</FormHelperText>}
                        </FormControl>
                      </Grid>                     
                      <Grid item xs={12}>
                        {/* <FormControl
                          component="fieldset"
                          error={!!errors.regular}
                          size="small"
                          sx={{ 
                            display: "flex", 
                            flexDirection: "row", 
                            alignItems: "center",
                            width: "100%",
                            minHeight: "40px",
                            mt: -0.25,
                            pt: 0
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={client.regular === true}
                                onChange={handleRegularChange}
                                disabled={isLoading}
                              />
                            }
                            label="Regular"
                            sx={{ 
                              mr: 2,
                              my: 0,
                              "& .MuiTypography-root": {
                                fontSize: "0.875rem",
                              },
                              "& .MuiCheckbox-root": {
                                padding: "4px",
                              },
                            }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={client.regular === false}
                                onChange={handleNonRegularChange}
                                disabled={isLoading}
                              />
                            }
                            label="Non-regular"
                            sx={{
                              my: 0,
                              "& .MuiTypography-root": {
                                fontSize: "0.875rem",
                              },
                              "& .MuiCheckbox-root": {
                                padding: "4px",
                              },
                            }}
                          />
                          {!!errors.regular && (
                            <FormHelperText>{errors.regular}</FormHelperText>
                          )}
                        </FormControl> */}
                        <FormControl component="fieldset">
                          <FormLabel component="legend">Client Type</FormLabel>
                          <RadioGroup
                            row
                            value={String(client.regular)}
                            onChange={handleRegularChange}
                          >
                            <FormControlLabel
                              value="true"
                              control={<Radio />}
                              label="Regular"
                            />
                            <FormControlLabel
                              value="false"
                              control={<Radio />}
                              label="Non-Regular"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <StyledHr />
                <Grid container sx={{ mt: 2 }}>
                  <Grid item xs={12}>
                    <CardHeader
                      title={
                        <Typography
                          variant="h6"
                          sx={{
                            paddingBottom: "0.5rem",
                            fontWeight: "bold",
                            textTransform: "uppercase",
                            fontSize: "1rem",
                          }}
                        >
                          Billing
                        </Typography>
                      }
                      sx={{ pb: 0, pt: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        <TextField
                          label="Price"
                          fullWidth
                          value={client.price}
                          onChange={(e) =>
                            handlePriceChange(e.target.value)
                          }
                          size="small"
                          disabled={isLoading}
                          error={!!errors.price}
                          helperText={errors.price}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        <TextField
                          label="Payment Terms"
                          fullWidth
                          value={client.payment_terms}
                          onChange={(e) =>
                            handlePaymentTermsChange(e.target.value)
                          }
                          size="small"
                          disabled={isLoading}
                          error={!!errors.payment_terms}
                          helperText={errors.payment_terms}
                        />
                      </Grid>                     
                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            label="Contract Start"
                            value={client.contract_start}
                            onChange={handleContractStartChange}
                            renderInput={params => (
                              <TextField
                                {...params}
                                size="small"
                                fullWidth
                                error={!!errors.contract_start}
                                helperText={errors.contract_start}
                              />
                            )}
                            disabled={isLoading}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            label="Contract End"
                            value={client.contract_end}
                            onChange={handleContractEndChange}
                            disabled={!client.contract_start}      // optional: only enable once a start is picked
                            renderInput={params => (
                              <TextField
                                {...params}
                                size="small"
                                fullWidth
                                error={!!errors.contract_end}
                                helperText={errors.contract_end}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Form buttons */}
                <Grid
                  item
                  xs={12}
                  display="flex"
                  justifyContent={{
                    xs: "center",
                    md: "center",
                    lg: "flex-end",
                  }}
                  alignItems="center"
                  mt={2}
                  mb={2}
                  mr={2}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate("/manage-clients")}
                    sx={{
                      fontSize: "0.875rem",
                      padding: "8px 16px",
                      color: "#FF4C4C",
                      borderColor: "#FF4C4C",
                    }}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    sx={{
                      marginLeft: "10px",
                      fontSize: "0.875rem",
                      padding: "8px 16px",
                    }}
                    disabled={isLoading}
                  >
                    Submit
                  </Button>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>
    </BaseFormWrapper>
  );
}

export default CreateClient;
